import { useState, useEffect, useRef, useCallback } from 'react';
import {
  apiStartDriverReports,
  apiGetDriverReports,
  apiApplyDriverReportsChanges,
  apiSkipDriverReportsChanges,
  apiMarkDiscrepancyViewed,
  apiProceedWithoutMVR,
} from 'api/quote-marketplace';
import type {
  DriverReportsStatus,
  DriverReportsResponse,
  DriverReportsDecision,
  Discrepancy,
  MVRErrorDetails,
} from 'api/quote-marketplace';

const POLL_INTERVAL_MS = 3000;

interface UseDriverReportsProps {
  quoteId: number;
  enabled?: boolean;
  onComplete?: () => void;
  onApplyChangesComplete?: (redirectTo?: string) => void;
  onSkipChangesComplete?: () => void;
  onProceedWithoutMVRComplete?: () => void;
  onError?: (error: string) => void;
}

export interface UseDriverReportsReturn {
  status: DriverReportsStatus | null;
  discrepancies: Discrepancy[];
  hasDiscrepancies: boolean;
  viewedIndices: number[];
  unviewedCount: number;
  decision: DriverReportsDecision;
  error: string | null;
  errorDetails: MVRErrorDetails | null;
  canRetry: boolean;
  canProceedWithoutMVR: boolean;
  successfulDriverCount: number;
  failedDriverCount: number;
  isValidating: boolean;
  isApplying: boolean;
  isSkipping: boolean;
  isProceedingWithoutMVR: boolean;
  startValidation: () => Promise<void>;
  retryValidation: () => Promise<void>;
  applyChanges: () => Promise<void>;
  skipChanges: () => Promise<void>;
  proceedWithoutMVR: () => Promise<void>;
  markViewed: (index: number) => Promise<void>;
  reset: () => void;
}

export const useDriverReports = ({
  quoteId,
  enabled = true,
  onComplete,
  onApplyChangesComplete,
  onSkipChangesComplete,
  onProceedWithoutMVRComplete,
  onError,
}: UseDriverReportsProps): UseDriverReportsReturn => {
  const [status, setStatus] = useState<DriverReportsStatus | null>(null);
  const [discrepancies, setDiscrepancies] = useState<Discrepancy[]>([]);
  const [viewedIndices, setViewedIndices] = useState<number[]>([]);
  const [decision, setDecision] = useState<DriverReportsDecision>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<MVRErrorDetails | null>(
    null,
  );
  const [canRetry, setCanRetry] = useState(false);
  const [canProceedWithoutMVR, setCanProceedWithoutMVR] = useState(false);
  const [successfulDriverCount, setSuccessfulDriverCount] = useState(0);
  const [failedDriverCount, setFailedDriverCount] = useState(0);
  const [isValidating, setIsValidating] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [isProceedingWithoutMVR, setIsProceedingWithoutMVR] = useState(false);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const isActionTriggeredRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const handleResponse = useCallback(
    (response: DriverReportsResponse) => {
      if (!mountedRef.current) return;

      setStatus(response.status);

      if (response.status === 'complete') {
        stopPolling();
        setIsValidating(false);
        setDiscrepancies(response.discrepancies || []);
        setViewedIndices(response.viewed_indices || []);
        setDecision(response.decision || null);
        setErrorDetails(response.error_details || null);
        setCanRetry(response.can_retry || false);
        setCanProceedWithoutMVR(false);
        setSuccessfulDriverCount(response.successful_driver_count || 0);
        setFailedDriverCount(response.failed_driver_count || 0);
        if (
          isActionTriggeredRef.current &&
          !response.has_discrepancies &&
          (!response.decision || response.decision === 'skipped_no_license')
        ) {
          onComplete?.();
        }
        isActionTriggeredRef.current = false;
      } else if (response.status === 'error') {
        stopPolling();
        setIsValidating(false);
        setError(response.error || 'Validation failed');
        setErrorDetails(response.error_details || null);
        setCanRetry(response.can_retry || false);
        setCanProceedWithoutMVR(response.can_proceed_without_mvr || false);
        setSuccessfulDriverCount(response.successful_driver_count || 0);
        setFailedDriverCount(response.failed_driver_count || 0);
        if (isActionTriggeredRef.current) {
          onError?.(response.error || 'Validation failed');
        }
        isActionTriggeredRef.current = false;
      }
    },
    [stopPolling, onComplete, onError],
  );

  const pollStatus = useCallback(async () => {
    try {
      const response = await apiGetDriverReports(quoteId);
      handleResponse(response);
    } catch (err) {}
  }, [quoteId, handleResponse]);

  const startValidation = useCallback(async () => {
    isActionTriggeredRef.current = true;
    setIsValidating(true);
    setError(null);
    setErrorDetails(null);
    setCanRetry(false);
    setCanProceedWithoutMVR(false);
    setDiscrepancies([]);

    try {
      const response = await apiStartDriverReports(quoteId);
      handleResponse(response);

      if (response.status === 'in_progress' && !pollingRef.current) {
        pollingRef.current = setInterval(pollStatus, POLL_INTERVAL_MS);
      }
    } catch (err) {
      setIsValidating(false);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to start validation';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  }, [quoteId, handleResponse, pollStatus, onError]);

  const retryValidation = useCallback(async () => {
    setError(null);
    setErrorDetails(null);
    setCanRetry(false);
    setCanProceedWithoutMVR(false);
    await startValidation();
  }, [startValidation]);

  const applyChanges = useCallback(async () => {
    isActionTriggeredRef.current = true;
    setIsApplying(true);

    try {
      const response = await apiApplyDriverReportsChanges(quoteId);
      setIsApplying(false);
      setStatus(null);
      setDiscrepancies([]);
      setViewedIndices([]);
      onApplyChangesComplete?.(response.redirect_to);
    } catch (err) {
      setIsApplying(false);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to apply changes';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  }, [quoteId, onApplyChangesComplete, onError]);

  const skipChanges = useCallback(async () => {
    isActionTriggeredRef.current = true;
    setIsSkipping(true);

    try {
      await apiSkipDriverReportsChanges(quoteId);
      setIsSkipping(false);
      setStatus(null);
      setDiscrepancies([]);
      setViewedIndices([]);
      onSkipChangesComplete?.();
    } catch (err) {
      setIsSkipping(false);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to skip changes';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  }, [quoteId, onSkipChangesComplete, onError]);

  const proceedWithoutMVR = useCallback(async () => {
    isActionTriggeredRef.current = true;
    setIsProceedingWithoutMVR(true);

    try {
      await apiProceedWithoutMVR(quoteId);
      setIsProceedingWithoutMVR(false);
      setStatus(null);
      setError(null);
      setErrorDetails(null);
      setCanRetry(false);
      setCanProceedWithoutMVR(false);
      setDecision('proceeded_without_mvr');
      onProceedWithoutMVRComplete?.();
    } catch (err) {
      setIsProceedingWithoutMVR(false);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to proceed without MVR';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  }, [quoteId, onProceedWithoutMVRComplete, onError]);

  const markViewed = useCallback(
    async (index: number) => {
      try {
        const response = await apiMarkDiscrepancyViewed(quoteId, index);
        setViewedIndices(response.viewed_indices);
      } catch (err) {}
    },
    [quoteId],
  );

  const reset = useCallback(() => {
    stopPolling();
    setStatus(null);
    setDiscrepancies([]);
    setViewedIndices([]);
    setDecision(null);
    setError(null);
    setErrorDetails(null);
    setCanRetry(false);
    setCanProceedWithoutMVR(false);
    setSuccessfulDriverCount(0);
    setFailedDriverCount(0);
    setIsValidating(false);
    setIsApplying(false);
    setIsSkipping(false);
    setIsProceedingWithoutMVR(false);
  }, [stopPolling]);

  useEffect(() => {
    mountedRef.current = true;

    if (!enabled) {
      return () => {
        mountedRef.current = false;
        stopPolling();
      };
    }

    const fetchInitialState = async () => {
      try {
        const response = await apiGetDriverReports(quoteId);
        handleResponse(response);

        if (response.status === 'in_progress' && !pollingRef.current) {
          setIsValidating(true);
          pollingRef.current = setInterval(pollStatus, POLL_INTERVAL_MS);
        }
      } catch (err) {}
    };

    fetchInitialState();

    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [quoteId, enabled, handleResponse, stopPolling]);

  const unviewedCount = discrepancies.length - viewedIndices.length;

  return {
    status,
    discrepancies,
    hasDiscrepancies: discrepancies.length > 0,
    viewedIndices,
    unviewedCount,
    decision,
    error,
    errorDetails,
    canRetry,
    canProceedWithoutMVR,
    successfulDriverCount,
    failedDriverCount,
    isValidating,
    isApplying,
    isSkipping,
    isProceedingWithoutMVR,
    startValidation,
    retryValidation,
    applyChanges,
    skipChanges,
    proceedWithoutMVR,
    markViewed,
    reset,
  };
};
